
/* ============================== src/components/views/TasksView.jsx ============================== */
// path: frontend/src/components/views/TasksView.jsx
/**
 * Вкладка «Задачи». Серверная пагинация и фильтрация.
 * Глобальный поиск задач работает через облегченный эндпоинт /tasks/lookup.
 *
 * Режим «контекст проекта» (правило C/D ТЗ):
 *   - при initialProjectId проект открыт из карточки проекта → ходим в
 *     GET /projects/:projectId/tasks через useProjectTasksQuery, а не в
 *     глобальный /tasks. Сервер сам проверит видимость проекта (404) и
 *     вернёт ВСЕ задачи проекта без пофильтровой видимости задач.
 *
 * Тумблер «Все задачи» (filters.showAll, default=false) — только в
 * глобальном режиме:
 *   - ВЫКЛ — только свои: в queryParams выставляется assigneeId=ur.id
 *     (серверная фильтрация — пагинация и totalPages сохраняются).
 *   - ВКЛ — всё, что роль вправе видеть (бэк отдаёт scope роли).
 *   - Тумблер рендерится только при capabilities.seeAllTasks
 */
import { useState, useMemo, useRef, memo } from "react";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../hooks/useAuth";
import Kanban from "../Kanban";
import { Select } from "../Select";
import { EntitySelectDropdown } from "../EntitySelectDropdown";
import { AsyncEntitySelectDropdown } from "../AsyncEntitySelectDropdown";
import { api } from "../../api/client";
import { SortControl } from "../SortControl";
import { ToggleSwitch } from "../ToggleSwitch";
import { ViewFooter } from "../ViewFooter";
import { ViewModeToggle } from "../ViewModeToggle";
import { ListLayoutToggle } from "../ListLayoutToggle";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import TasksList from "./TasksList";
import { TaskCard } from "../cards/TaskCard";
import { useFilters } from "../../hooks/useFilters";
import { useSort, withSortDefaults } from "../../hooks/useSort";
import { useFilterOptions } from "../../hooks/useFilterOptions";
import { useTasksViewDb } from "../../hooks/useDb";
import { useDataHelpers } from "../../hooks/useDataHelpers";
import { useUnreadCommentIndex } from "../../hooks/useChatStats";
import { useTaskActions } from "../../hooks/useTaskActions";
import { useEventCallback } from "../../hooks/useEventCallback";
import { useTasksQuery, useProjectTasksQuery } from "../../api/queries/tasks";
import {
  TASK_STATUSES,
  TASK_STATUS_ORDER,
  PRIORITIES,
} from "../../utils/constants";
import { chatKey } from "../../utils/chatKey";
import {
  optionsFromMap,
  toEmployeeOption,
  toDeptOption,
} from "../../utils/selectOptions";
import { filterSelectClass, filterDateClass } from "../../utils/filters";

const INITIAL_FILTERS = withSortDefaults({
  projectId: "all",
  taskId: "all",
  assigneeId: "all",
  priority: "all",
  deptId: "all",
  tag: "all",
  deadlineBefore: "",
  showAll: false,
});

const SORT_OPTIONS = [
  { value: "priority", label: "По приоритету" },
  { value: "deadline", label: "По сроку" },
  { value: "title", label: "По названию" },
  { value: "assignee", label: "По исполнителю" },
  { value: "project", label: "По проекту" },
  { value: "planned", label: "По плановым часам" },
  { value: "fact", label: "По фактическим часам" },
];

const NUMERIC_SORTS = new Set(["planned", "fact"]);
const PRIORITY_SELECT_OPTIONS = optionsFromMap(PRIORITIES, "Приоритет");

const projectOptionValue = (p) => p.id;
const projectOptionLabel = (p) => (p.code ? `${p.code} - ${p.name}` : p.name);

function TasksView({
  ur,
  openTask,
  openProject,
  openCopyTask,
  openTemplateFromTask,
  openTag,
  initialProjectId = null,
}) {
  const { capabilities } = useAuth();
  const refDb = useTasksViewDb();
  const { employees, projects, departments } = refDb;

  const employeesById = useMemo(
    () => new Map(employees.map((e) => [e.id, e])),
    [employees],
  );
  const projectsById = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects],
  );

  const { showToast } = useToast();
  const { getTaskSpent } = useDataHelpers(refDb);

  const canSeeAll = Boolean(capabilities.seeAllTasks);
  const unreadIndex = useUnreadCommentIndex(ur.id);

  const isProjectContext = Boolean(initialProjectId);

  const [viewMode, setViewMode] = useState("kanban");
  const [listLayout, setListLayout] = useState("tiles");
  const [page, setPage] = useState(1);
  const [isDeadlineFocused, setIsDeadlineFocused] = useState(false);
  const deadlineInputRef = useRef(null);

  const { filters, setFilter } = useFilters(INITIAL_FILTERS);
  const { showAll } = filters;

  const {
    field: sortField,
    dir: sortDir,
    handleFieldChange: handleSortFieldChange,
    handleDirToggle: handleSortDirToggle,
  } = useSort({ filters, setFilter, numericFields: NUMERIC_SORTS });

  const isKanban = viewMode === "kanban";

  const queryParams = useMemo(() => {
    const params = {
      ...filters,
      sortField,
      sortDir,
      mode: isKanban ? "kanban" : "list",
    };

    if (isProjectContext) {
      params.projectId = initialProjectId;
      params.includeDrafts = true;
      delete params.showAll;
    } else if (canSeeAll && !filters.showAll && filters.assigneeId === "all") {
      // «Только свои»: бэкенд вернёт задачи, где я автор или исполнитель.
      params.mine = true;
    }

    if (!isKanban) {
      params.page = page;
      params.limit = 50;
    }
    Object.keys(params).forEach(
      (key) =>
        (params[key] === "all" || params[key] === "") && delete params[key],
    );
    return params;
  }, [
    filters,
    sortField,
    sortDir,
    isKanban,
    page,
    canSeeAll,
    isProjectContext,
    initialProjectId,
  ]);

  const globalQuery = useTasksQuery(queryParams, {
    enabled: !isProjectContext,
  });
  const projectQuery = useProjectTasksQuery(initialProjectId, queryParams, {
    enabled: isProjectContext,
  });

  const data = isProjectContext ? projectQuery.data : globalQuery.data;
  const isPlaceholderData = isProjectContext
    ? projectQuery.isPlaceholderData
    : globalQuery.isPlaceholderData;

  const tasks = useMemo(() => data?.items ?? [], [data]);
  const totals = useMemo(() => data?.totals ?? {}, [data]);
  const totalPages = data?.meta?.totalPages ?? 1;

  const db = useMemo(() => ({ ...refDb, tasks }), [refDb, tasks]);

  const projOptions = useMemo(
    () => projects.filter((p) => !p.archived),
    [projects],
  );
  const execOptions = useMemo(() => employees, [employees]);

  const { handleMove: handleMoveTask, handleDelete: handleDeleteTask } =
    useTaskActions({ ur, db, toast: showToast });

  const renderTaskCard = useEventCallback((task) => {
    const project = projectsById.get(task.projectId);
    const assignee = task.assigneeId
      ? employeesById.get(task.assigneeId)
      : null;
    const spent = getTaskSpent(task);
    const unread = unreadIndex.get(chatKey({ taskId: task.id })) || 0;
    return (
      <TaskCard
        task={task}
        project={project}
        assignee={assignee}
        spent={spent}
        unread={unread}
        db={db}
        user={ur}
        openTask={openTask}
        openTag={openTag}
        onMove={handleMoveTask}
        onDelete={handleDeleteTask}
        onCopy={openCopyTask}
        onMakeTemplate={openTemplateFromTask}
      />
    );
  });

  const handleDeadlineBeforeChange = (e) => {
    const value = e.target.value;
    setFilter("deadlineBefore", value);
    setPage(1);
    if (!value) {
      setIsDeadlineFocused(false);
      deadlineInputRef.current?.blur();
    }
  };

  const handleFilterChange = (key, val) => {
    setFilter(key, val);
    setPage(1);
  };

  const handleShowAllChange = useEventCallback((v) => {
    handleFilterChange("showAll", v);
  });

  const assigneeSelectOptions = useFilterOptions(
    execOptions,
    "Исполнитель",
    toEmployeeOption,
  );
  const deptSelectOptions = useFilterOptions(
    departments,
    "Отдел",
    toDeptOption,
  );
  const canCreate = Boolean(capabilities.createTask);

  return (
    <>
      <div className="toolbar toolbar-wrap">
        {!isProjectContext && (
          <AsyncEntitySelectDropdown
            fetchOptions={({ search }) =>
              api.get("/tasks/lookup", { params: { search, limit: 50 } })
            }
            queryKey={["tasks", "lookup"]}
            value={filters.taskId || "all"}
            onChange={(v) => handleFilterChange("taskId", v)}
            getOptionValue={(t) => t.id}
            getOptionLabel={(t) => t.title}
            allLabel="Все задачи"
            placeholder="Поиск задачи..."
            className="filter-project-select"
            fallbackItem={tasks.find((t) => t.id === filters.taskId) || null}
          />
        )}

        {!isProjectContext && (
          <EntitySelectDropdown
            items={projOptions}
            value={filters.projectId}
            onChange={(v) => handleFilterChange("projectId", v)}
            getOptionValue={projectOptionValue}
            getOptionLabel={projectOptionLabel}
            allLabel="Все проекты"
            placeholder="Проект"
            searchable
            className="filter-project-select"
          />
        )}

        {!isProjectContext && (
          <Select
            className={filterSelectClass(filters.assigneeId)}
            value={filters.assigneeId}
            onChange={(v) => handleFilterChange("assigneeId", v)}
            options={assigneeSelectOptions}
          />
        )}

        <Select
          className={filterSelectClass(filters.priority)}
          value={filters.priority}
          onChange={(v) => handleFilterChange("priority", v)}
          options={PRIORITY_SELECT_OPTIONS}
        />
        <Select
          className={filterSelectClass(filters.deptId, "filter-select-dept")}
          value={filters.deptId}
          onChange={(v) => handleFilterChange("deptId", v)}
          options={deptSelectOptions}
        />
        <span
          className={filterDateClass(filters.deadlineBefore, isDeadlineFocused)}
        >
          <input
            ref={deadlineInputRef}
            type="date"
            className="inp filter-date-input"
            value={filters.deadlineBefore}
            onChange={handleDeadlineBeforeChange}
            onFocus={() => setIsDeadlineFocused(true)}
            onBlur={() => setIsDeadlineFocused(false)}
            aria-label="Срок до"
          />
          {!filters.deadlineBefore && (
            <span className="filter-date-hint" aria-hidden="true">
              Срок до
            </span>
          )}
        </span>
        <SortControl
          options={SORT_OPTIONS}
          field={sortField}
          dir={sortDir}
          onFieldChange={handleSortFieldChange}
          onToggleDir={handleSortDirToggle}
        />
        {canCreate && (
          <button
            type="button"
            className="btn primary toolbar-action"
            onClick={() => openTask(null)}
          >
            <Ic d={ICONS.plus} size={15} /> Создать
          </button>
        )}
      </div>

      <div className={isPlaceholderData ? "tasks-view-loading" : undefined}>
        {isKanban ? (
          <Kanban
            items={tasks}
            totals={totals}
            statusOrder={TASK_STATUS_ORDER}
            statusMap={TASK_STATUSES}
            renderCard={renderTaskCard}
            onDrop={handleMoveTask}
          />
        ) : (
          <TasksList
            tasks={tasks}
            db={db}
            user={ur}
            unreadIndex={unreadIndex}
            layout={listLayout}
            openTask={openTask}
            openProject={openProject}
            openTag={openTag}
            onMove={handleMoveTask}
            onDelete={handleDeleteTask}
            onCopy={openCopyTask}
            onMakeTemplate={openTemplateFromTask}
          />
        )}
      </div>

      <ViewFooter>
        <ViewModeToggle
          value={viewMode}
          onChange={(v) => {
            setViewMode(v);
            setPage(1);
          }}
        />
        {viewMode === "list" && (
          <ListLayoutToggle value={listLayout} onChange={setListLayout} />
        )}
        {viewMode === "list" && totalPages > 1 && (
          <div className="flex gap-2 items-center ml-4">
            <button
              className="btn ghost sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Назад
            </button>
            <span className="text-sm">
              {page} / {totalPages}
            </span>
            <button
              className="btn ghost sm"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Вперед
            </button>
          </div>
        )}
        {canSeeAll && !isProjectContext && (
          <ToggleSwitch
            checked={showAll}
            onChange={handleShowAllChange}
            label="Все задачи"
          />
        )}
      </ViewFooter>
    </>
  );
}

export default memo(TasksView);
